import { useEffect, useState } from "react";
import axiosInstance from "../../api/axiosInstance";
import { ChevronDown, RefreshCcw, Trash2 } from "lucide-react";
import { CircularProgress } from "@mui/material";

const Marketplace = () => {
  const [locationId, setLocationId] = useState("");
  const [staffUsers, setStaffUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [expandedTemplates, setExpandedTemplates] = useState({});
  const [templateGroupsData, setTemplateGroupsData] = useState({});
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState({});
  const [showAddTemplate, setShowAddTemplate] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [deleteTemplateId, setDeleteTemplateId] = useState(null);
  const [deletingTemplate, setDeletingTemplate] = useState(false);
  const [syncing, setSyncing] = useState(false); // <-- new state for sync spinner

  useEffect(() => {
    const pathParts = window.location.pathname.split("/");
    const locIndex = pathParts.indexOf("location");
    setLocationId(locIndex !== -1 && pathParts[locIndex + 1] ? pathParts[locIndex + 1] : "C6ZqOQs06oW19eEPN45F");
  }, []);

  const handleSync = async () => {
    if (!locationId) return;
    setSyncing(true);
    try {
      await axiosInstance.post("/api/webhooks/sync-ghl-staff-users", { locationId });
      await Promise.all([fetchStaffUsers(), fetchTemplates()]);
    } catch (err) {
      console.error("Error syncing staff users", err);
    } finally {
      setSyncing(false);
    }
  };

  const fetchStaffUsers = async () => {
    if (!locationId) return;
    setLoadingUsers(true);
    try {
      const res = await axiosInstance.get(`api/webhooks/staff/users?locationId=${locationId}`);
      setStaffUsers(res.data?.success ? res.data.data || [] : []);
    } catch (err) {
      console.error("Error fetching staff users", err);
      setStaffUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchTemplates = async () => {
    if (!locationId) return;
    setLoadingTemplates(true);
    try {
      const res = await axiosInstance.get(`/api/webhooks/ghl-staff-templates?locationId=${locationId}`);
      setTemplates(res.data?.success ? res.data.data || [] : []);
      // if (res.data?.staffUsers) setStaffUsers(res.data.staffUsers || []);
    } catch (err) {
      console.error("Error fetching templates", err);
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const fetchTemplateGroups = async (templateId) => {
    if (!locationId || !templateId) return;
    setLoadingGroups((prev) => ({ ...prev, [templateId]: true }));
    try {
      const res = await axiosInstance.get(`/api/webhooks/get-ghl-groups-fields?locationId=${locationId}&templateId=${templateId}`);
      const groups = res.data.groups || [];
      setTemplateGroupsData((prev) => ({
        ...prev,
        [templateId]: groups.map((g) => ({
          ...g,
          open: false,
          fields: (g.fields || []).map((f) => ({ ...f, isVisible: f.isVisible ?? true })),
        })),
      }));
    } catch (err) {
      console.error("Error fetching template groups", err);
      setTemplateGroupsData((prev) => ({ ...prev, [templateId]: [] }));
    } finally {
      setLoadingGroups((prev) => ({ ...prev, [templateId]: false }));
    }
  };

  // initial fetch for staff users and templates
  useEffect(() => {
    fetchStaffUsers();
    fetchTemplates();
    // eslint-disable-next-line
  }, [locationId]);

  const toggleTemplate = (templateId) => {
    const willOpen = !expandedTemplates[templateId];
    setExpandedTemplates((prev) => ({ ...prev, [templateId]: willOpen }));
    if (willOpen) fetchTemplateGroups(templateId);
  };

  const toggleGroup = (templateId, groupKey) => {
    setTemplateGroupsData((prev) => ({
      ...prev,
      [templateId]: (prev[templateId] || []).map((g) => (g.groupKey === groupKey ? { ...g, open: !g.open } : g)),
    }));
  };

  const handleAddTemplate = async () => {
    const templateName = newTemplateName.trim();
    if (!templateName) return;

    setSavingTemplate(true);
    try {
      await axiosInstance.post("/api/webhooks/ghl-staff-templates", { group_name: templateName, locationId });
      setNewTemplateName("");
      setShowAddTemplate(false);
      fetchTemplates();
    } catch (err) {
      console.error("Error adding template", err);
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!deleteTemplateId) return;

    setDeletingTemplate(true);
    try {
      await axiosInstance.delete(`/api/webhooks/ghl-staff-templates/${deleteTemplateId}`);

      setTemplates((prev) => prev.filter((t) => t.id !== deleteTemplateId));

      setDeleteTemplateId(null);
    } catch (err) {
      console.error("Error deleting template", err);
    } finally {
      setDeletingTemplate(false);
    }
  };

  const handleStaffTemplateChange = async (templateId, staffId, checked) => {
    const staffUser = staffUsers.find((u) => u.staff_id === staffId);

    setTemplates((prev) =>
      prev.map((template) => {
        const filteredStaff = (template.staff || []).filter((staff) => staff.staff_id !== staffId);
        if (Number(template.id) === Number(templateId) && checked && staffUser) {
          return { ...template, staff: [...filteredStaff, staffUser] };
        }
        return { ...template, staff: filteredStaff };
      }),
    );

    try {
      await axiosInstance.put("/api/webhooks/ghl-staff-template-staff", {
        templateId,
        staffId,
        checked,
        locationId,
      });
      fetchTemplates();
    } catch (err) {
      console.error("Error updating template staff", err);
      fetchTemplates();
    }
  };

  const handleFieldVisibilityChange = async (templateId, groupKey, fieldKey, newValue) => {
    setTemplateGroupsData((prev) => ({
      ...prev,
      [templateId]: (prev[templateId] || []).map((g) => (g.groupKey === groupKey ? { ...g, fields: g.fields.map((f) => (f.fieldKey === fieldKey ? { ...f, isVisible: newValue } : f)) } : g)),
    }));

    try {
      await axiosInstance.put("/api/webhooks/ghl-groups-fields", {
        locationId,
        templateId,
        groupKey,
        fieldKey,
        isVisible: newValue,
      });
    } catch (err) {
      console.error("Error updating field visibility", err);
      fetchTemplateGroups(templateId);
    }
  };

  const handleGroupVisibilityChange = async (templateId, group, newValue) => {
    setTemplateGroupsData((prev) => ({
      ...prev,
      [templateId]: (prev[templateId] || []).map((g) => (g.groupKey === group.groupKey ? { ...g, isVisible: newValue, fields: g.fields.map((f) => ({ ...f, isVisible: newValue })) } : g)),
    }));

    try {
      await axiosInstance.put("/api/webhooks/ghl-groups-fields", {
        locationId,
        templateId,
        groupKey: group.groupKey,
        fieldKeys: (group.fields || []).map((field) => ({ fieldKey: field.fieldKey, fieldLabel: field.fieldLabel })),
        isVisible: newValue,
      });
    } catch (err) {
      console.error("Error updating group visibility", err);
      fetchTemplateGroups(templateId);
    }
  };

  return (
    <div className="!px-4">
      <div className="bg-[#ff850f] !my-4 !px-4 !py-4 rounded-xl !flex !items-center !justify-between !gap-4">
        <h1 className="!font-semibold !text-white !text-lg">Staff Templates</h1>
        <button onClick={() => setShowAddTemplate(true)} className="!bg-white !text-[#ff850f] !font-semibold !px-4 !py-2 !rounded-lg">
          Add New Template
        </button>
      </div>

      {loadingTemplates || loadingUsers ? (
        <div className="!flex !items-center !gap-2 !text-gray-500">
          <CircularProgress color="inherit" size={20} />
          <p>Loading templates...</p>
        </div>
      ) : (
        <div className="!mt-4 grid">
          {templates.map((template) => {
            const isOpen = !!expandedTemplates[template.id];
            const assignedStaff = template.staff || [];
            const groups = templateGroupsData[template.id] || [];

            return (
              <div key={template.id} className="!rounded-lg !shadow-sm !mb-3 !bg-white !overflow-hidden">
                <div className="!flex !items-center !justify-between !px-4 !py-3 !gap-4">
                  <button onClick={() => toggleTemplate(template.id)} className="!flex !items-center !space-x-2 !text-sky-800 !cursor-pointer">
                    <ChevronDown size={18} className={`!ease-in-out !duration-150 ${isOpen ? "-rotate-90" : "rotate-0"}`} />
                    <h2 className="!font-semibold !text-md">{template.group_name}</h2>
                  </button>
                  <div className="!text-sm !text-gray-600 !text-right flex gap-4 items-center">
                    <span>{assignedStaff.length ? assignedStaff.map((staff) => staff.staff_name).join(", ") : "No staff assigned"}</span>
                    <Trash2 size={18} className="text-red-500 cursor-pointer hover:text-red-700" onClick={() => setDeleteTemplateId(template.id)} />
                  </div>
                </div>

                <div className={`!transition-all !duration-300 ${isOpen ? "!max-h-[5000px] !p-4" : "!max-h-0 !p-0"} !overflow-hidden`}>
                  <div className="!mb-5">
                    <div className="flex items-center justify-center space-x-4 mb-3">
                      <h3 className="!font-semibold !text-gray-700">Staff Members</h3>
                      <div>{syncing ? <CircularProgress size={14} /> : <RefreshCcw size={14} className="cursor-pointer" onClick={handleSync} />}</div>
                    </div>
                    <div className="grid md:grid-cols-3 !gap-3">
                      {staffUsers.map((staff) => (
                        <label key={staff.staff_id} className="!flex !items-center !gap-2 !text-sm !text-gray-700">
                          <input type="checkbox" checked={assignedStaff.some((assigned) => assigned.staff_id === staff.staff_id)} onChange={(e) => handleStaffTemplateChange(template.id, staff.staff_id, e.target.checked)} className="!cursor-pointer" />
                          {staff.staff_name}
                        </label>
                      ))}
                    </div>
                  </div>

                  {loadingGroups[template.id] ? (
                    <p className="!text-gray-500">Loading groups and fields...</p>
                  ) : (
                    groups.map((group) => {
                      const allFieldsVisible = (group.fields || []).length ? group.fields.every((field) => field.isVisible) : group.isVisible;

                      return (
                        <div key={group.groupKey} className="!rounded-lg !shadow-sm !mb-3 !bg-gray-50 !overflow-hidden">
                          <div className="!flex !items-center !justify-between !px-4 !py-3">
                            <div className="!flex !items-center !space-x-3">
                              <input type="checkbox" checked={!!allFieldsVisible} onChange={(e) => handleGroupVisibilityChange(template.id, group, e.target.checked)} className="!cursor-pointer" />
                              <button onClick={() => toggleGroup(template.id, group.groupKey)} className="!flex !items-center !space-x-2 !text-sky-800 !cursor-pointer">
                                <ChevronDown size={18} className={`!ease-in-out !duration-150 ${group.open ? "-rotate-90" : "rotate-0"}`} />
                                <h2 className="!font-semibold !text-md">{group.groupLabel || group.groupKey}</h2>
                              </button>
                            </div>
                          </div>

                          <div className={`!transition-all !duration-300 ${group.open ? "!max-h-[1000px] !p-4" : "!max-h-0 !p-0"} !overflow-hidden`}>
                            {group.fields.map((field) => (
                              <div key={field.fieldKey} className="!mb-3 !text-left !flex !space-x-4 !items-center">
                                <input type="checkbox" checked={field.isVisible} onChange={(e) => handleFieldVisibilityChange(template.id, group.groupKey, field.fieldKey, e.target.checked)} className="!cursor-pointer" />
                                <label className="!block !font-medium">{field.fieldLabel || field.fieldKey}</label>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showAddTemplate && (
        <div className="!fixed !inset-0 !bg-black/40 !flex !items-center !justify-center !z-50">
          <div className="!bg-white !rounded-xl !p-5 !w-full !max-w-md">
            <h2 className="!font-semibold !text-lg !mb-4">Add New Template</h2>
            <input type="text" value={newTemplateName} onChange={(e) => setNewTemplateName(e.target.value)} placeholder="Template Name" className="!w-full !border !border-gray-300 !rounded-lg !px-3 !py-2 !mb-4" />
            <div className="!flex !justify-end !gap-3">
              <button onClick={() => setShowAddTemplate(false)} className="!px-4 !py-2 !rounded-lg !border !border-gray-300" disabled={savingTemplate}>
                Cancel
              </button>
              <button onClick={handleAddTemplate} className="!px-4 !py-2 !rounded-lg !bg-[#ff850f] !text-white" disabled={savingTemplate}>
                {savingTemplate ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTemplateId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-5 w-full max-w-md">
            <h2 className="font-semibold text-lg mb-2">Delete Template</h2>

            <p className="text-gray-600 mb-4">Are you sure you want to delete this template?</p>

            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteTemplateId(null)} className="px-4 py-2 border rounded-lg">
                Cancel
              </button>

              <button onClick={handleDeleteTemplate} className="px-4 py-2 bg-red-600 text-white rounded-lg">
                {deletingTemplate ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Marketplace;
